/**
 * Read-only accessors over a `Rule`. Each one returns the slice of the rule a
 * step or a filter cares about, resolving the action-type specific shape via
 * `typePath` so callers do not have to.
 *
 * These used to live scattered across `helpers.ts`, `Filters/helpers.ts`,
 * `Filters/Action/utils.ts`, `Steps/Actions/utils.ts` and
 * `Steps/CloudStorage/utils.ts`, with three of them defined more than once.
 * Keep new ones here, and keep them pure: no `State`, no dispatch, no mutation
 * — `helpers.ts` holds the predicates, factories and updaters.
 */
import {
  ApprovalSteps,
  ArchivingAddress,
  ArchivingAddressFilter,
  ContentFilter,
  ContentToSave,
  EmailFlow,
  EmailFlowFilter,
  Mailbox,
  MailboxFilter,
  SendDelay,
  SmimeEmails,
  Storage,
  StorageWebhook,
} from "@dashboard-v3/api";
import { Rule, RuleType } from "pages/Rules/common/types";

import { ActionState } from "../Steps/Actions/types";
import { ZipAttachments } from "@dashboard-v3/api";

// ------------------------------------------------------------------
// The primitive every other selector builds on: resolves the branch of the
// rule that holds the data for its action type.
// ------------------------------------------------------------------

// prettier-ignore
type ObjectType<T> = T extends "BLOCK_ATTACHMENTS"
  ? Rule["blockAttachments"]
  : T extends "COPY_EMAILS"
  ? Rule["copyEmails"]
  : T extends "MOVE_EMAILS"
  ? Rule["moveEmails"]
  : T extends "REPLACE_ATTACHMENTS"
  ? Rule["replaceAttachments"]
  : T extends "PROTECT_EMAILS"
  ? Rule["protectEmails"]
  : T extends "DRAG_AND_DROP"
  ? Rule["dragAndDrop"]
  : T extends "REQUEST_APPROVAL"
  ? Rule["requestApproval"]
  : T extends "SEND_DELAY"
  ? Rule["sendDelay"]
  : T extends "ZIP_ATTACHMENTS"
  ? Rule["zipAttachments"]
  : T extends "SMIME_EMAILS"
  ? Rule["smimeEmails"]
  : never;

export function typePath<T extends RuleType>(
  ruleType: T,
  rule: Rule
): ObjectType<T> {
  switch (ruleType) {
    case "BLOCK_ATTACHMENTS":
      return rule?.blockAttachments as ObjectType<T>;
    case "COPY_EMAILS":
      return rule?.copyEmails as ObjectType<T>;
    case "MOVE_EMAILS":
      return rule?.moveEmails as ObjectType<T>;
    case "REPLACE_ATTACHMENTS":
      return rule?.replaceAttachments as ObjectType<T>;
    case "PROTECT_EMAILS":
      return rule?.protectEmails as ObjectType<T>;
    case "DRAG_AND_DROP":
      return rule?.dragAndDrop as ObjectType<T>;
    case "REQUEST_APPROVAL":
      return rule?.requestApproval as ObjectType<T>;
    case "SEND_DELAY":
      return rule?.sendDelay as ObjectType<T>;
    case "ZIP_ATTACHMENTS":
      return rule?.zipAttachments as ObjectType<T>;
    case "SMIME_EMAILS":
      return rule?.smimeEmails as ObjectType<T>;
    default: {
      const exhaustiveCheck: never = ruleType;
      throw new Error(exhaustiveCheck);
    }
  }
}

// ------------------------------------------------------------------
// Action
// ------------------------------------------------------------------

export const getContentToSave = (rule: Rule): ContentToSave | null => {
  const path = typePath(rule.actionType, rule);
  if (path && "contentToSave" in path) return path.contentToSave;
  return null;
};

export const getLinksFormat = (rule: Rule) => {
  const path = typePath(rule.actionType, rule);
  if (path && "linksFormat" in path) return path.linksFormat;
  return null;
};

export const getActionState = (rule: Rule): ActionState => {
  const path = typePath(rule.actionType, rule);
  const action: ActionState = { content: null };

  if ("contentToSave" in path) {
    action.content = path.contentToSave.content;
  }
  if ("trash" in path) {
    action.trash = path.trash;
  }
  if ("moveOriginalToTrashOnReplace" in path) {
    action.moveOriginalToTrashOnReplace = path.moveOriginalToTrashOnReplace;
  }
  if ("linksFormat" in path) {
    action.linksFormat = path.linksFormat;
  }
  if ("blockNotification" in path) {
    action.blockNofication = path.blockNotification;
  }
  if ("passwordNotificationText" in path) {
    action.passwordNotificationText = path.passwordNotificationText;
  }

  if (rule.actionType === "ZIP_ATTACHMENTS") {
    const zipRule = path as ZipAttachments;

    action.passwordNotificationToRecipient =
      zipRule.passwordNotificationToRecipient;
    action.passwordNotificationToSender = zipRule.passwordNotificationToSender;

    if ("senderNotification" in zipRule) {
      action.senderNotification = zipRule.senderNotification;
    }
  }

  return action;
};

/**
 * The content filter, wherever the action keeps it: some hold it directly as
 * `contentFilter`, the ones that save content nest it under `contentToSave`.
 * No action type has both.
 */
export const getContentFilter = (rule: Rule): ContentFilter | null => {
  const path = typePath(rule.actionType, rule);
  if (!path) return null;
  if ("contentFilter" in path) return path.contentFilter;
  if ("contentToSave" in path) return path.contentToSave?.filter;
  return null;
};

// ------------------------------------------------------------------
// Target
// ------------------------------------------------------------------

export function getEmailFlow(rule: Rule): EmailFlow | null {
  const path = typePath(rule.actionType, rule);
  if (path && "emailFlow" in path) return path.emailFlow;
  return null;
}

export const getArchivingAddress = (rule: Rule): ArchivingAddress | null => {
  const path = typePath(rule.actionType, rule);
  if (path && "archivingAddress" in path) return path.archivingAddress;
  return null;
};

export const getMailbox = (rule: Rule): Mailbox | null => {
  const path = typePath(rule.actionType, rule);
  if (path && "mailbox" in path) return path.mailbox;
  return null;
};

export const getStorageWebhook = (rule: Rule): StorageWebhook | null => {
  const path = typePath(rule.actionType, rule);
  if (path && "storageWebhook" in path) return path.storageWebhook;
  return null;
};

// ------------------------------------------------------------------
// Target filters
// ------------------------------------------------------------------

export function emailFlowFilter(rule: Rule): EmailFlowFilter | null {
  const path = typePath(rule.actionType, rule);
  if (path && "emailFlow" in path) return path.emailFlow?.filter;
  return null;
}

export function mailboxFilter(rule: Rule): MailboxFilter | null {
  const path = typePath(rule.actionType, rule);
  if (path && "mailbox" in path) return path.mailbox?.filter;
  return null;
}

export function archivingAddressFilter(
  rule: Rule
): ArchivingAddressFilter | null {
  const path = typePath(rule.actionType, rule);
  if (path && "archivingAddress" in path) return path.archivingAddress?.filter;
  return null;
}

// ------------------------------------------------------------------
// Cloud storage
// ------------------------------------------------------------------

export const getCloudStorage = (rule: Rule): Storage | null => {
  const path = typePath(rule.actionType, rule);
  if (path && "storage" in path) return path.storage;
  return null;
};

// ------------------------------------------------------------------
// Approval steps / Temporary hold / S-MIME
// ------------------------------------------------------------------

export const getSupervisors = (rule: Rule) => {
  const path = typePath(rule.actionType, rule);
  if (path && "supervisors" in path) return path.supervisors;
  return null;
};

export const getApprovalSteps = (rule: Rule): ApprovalSteps => {
  if (
    rule.actionType !== "REQUEST_APPROVAL" &&
    rule.actionType !== "SEND_DELAY"
  ) {
    throw new Error("Action type is not request approval or send delay");
  }

  const path = typePath(rule.actionType, rule);
  const steps: Partial<ApprovalSteps> = {};

  if ("expiration" in path) steps.expiration = path.expiration;
  if ("supervisors" in path) steps.supervisors = path.supervisors;
  if ("senderNotification" in path) {
    steps.senderNotification = path.senderNotification;
  }
  if ("supervisorNotification" in path) {
    steps.supervisorNotification = path.supervisorNotification;
  }
  if ("reminderSenderNotification" in path) {
    steps.reminderSenderNotification = path.reminderSenderNotification;
  }
  if ("reminder" in path) steps.reminder = path.reminder;
  if ("doneNotification" in path)
    steps.doneNotification = path.doneNotification;

  return steps as ApprovalSteps;
};

export function getSendDelay(rule: Rule): Partial<SendDelay> {
  const path = typePath(rule.actionType, rule) as SendDelay;
  const sendDelay: Partial<SendDelay> = {};

  if ("expiration" in path) sendDelay.expiration = path.expiration;
  if ("reminder" in path) sendDelay.reminder = path.reminder;
  if ("rejectOnExpiration" in path) {
    sendDelay.rejectOnExpiration = path.rejectOnExpiration;
  }
  if ("senderNotification" in path) {
    sendDelay.senderNotification = path.senderNotification;
  }
  if ("reminderSenderNotification" in path) {
    sendDelay.reminderSenderNotification = path.reminderSenderNotification;
  }
  if ("doneNotification" in path) {
    sendDelay.doneNotification = path.doneNotification;
  }

  return sendDelay;
}

export function getSmimeActionState(rule: Rule) {
  const { actionType } = rule;

  if (actionType !== "SMIME_EMAILS") {
    throw new Error(
      `Rule type mismatch, expected SMIME_EMAILS, got ${actionType}`
    );
  }

  const { smimeActions, failedAction } = typePath(
    actionType,
    rule
  ) as SmimeEmails;

  return { smimeActions, failedAction };
}
